import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { Alert } from 'components/FbUI/Alert'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import {
  DS2026_MODAL_BODY_CLASS,
  DS2026_MODAL_BUTTON_CLASS,
  DS2026_MODAL_CONTENT_CLASS,
  DS2026_MODAL_FOOTER_CLASS,
  ds2026SelectStyles,
  dsControlClass,
} from 'components/FbUI/Ds2026Field'
import { FbModal as Modal } from '../../components/UI/Modal'
import ReactSelectWrapper from '../../components/FormUtils/ReactSelectWrapper'
import { getApiErrorDetail } from '../../utils/getApiErrorDetail'
import { useAuth } from '../../context/authentication/useAuth'
import { useGetCampaignLeads } from './leads_api'
import {
  useAttachCampaignSenderEmails,
  useGetCampaignSenderEmails,
  useGetWorkspaceSenderEmails,
} from './sender_emails_api'
import { useSendSequenceStepTestEmail } from 'services/sequences'

/** Pre-rendered subject/body sent through Bison's compose endpoint (no contact required). */
interface RenderedTestEmail {
  subject: string
  body: string
  plainText: boolean
  contactName: string
}

interface Props {
  show: boolean
  onHide: () => void
  bisonStepId: number | null
  bisonCampaignId: number
  stepNumber: number
  /**
   * Display label for the message under test, e.g. "Step 1 · Variant B". When
   * omitted, falls back to "Step {stepNumber}". Variants have no meaningful
   * sequential number, so callers pass the family-aware label here.
   */
  stepLabel?: string
  unresolvedVariables: string[]
  /**
   * Optional per-contact preview. When supplied, the test send uses these
   * already-substituted values instead of letting EmailBison fill variables
   * from a random campaign lead, so the recipient sees exactly what this
   * contact would receive.
   */
  renderedFor?: RenderedTestEmail | null
  /**
   * Fallback render used only when the campaign has no contacts. EmailBison's
   * random-lead endpoint 400s with zero contacts (FIR-7437), so we send this
   * caller-supplied sample (rendered against a sample contact) through the
   * compose path instead — which needs no contacts — so the test still works.
   */
  sampleRenderedFor?: RenderedTestEmail | null
}

/**
 * Modal for sending a sequence-step test email through EmailBison.
 *
 * Three modes:
 * - Per-contact (when `renderedFor` is set): caller pre-substitutes variables
 *   for a specific contact and the backend routes through Bison's compose
 *   endpoint so the recipient sees exactly that contact's rendered email.
 * - Sample (no `renderedFor`, campaign has no contacts): falls back to
 *   `sampleRenderedFor` through the same compose path, so the test still works
 *   before any contacts are attached (FIR-7437).
 * - Random contact (no `renderedFor`, campaign has contacts): backend hits
 *   Bison's `/test-email` endpoint and Bison fills variables from a random lead.
 */
export function SendStepTestEmailModal({
  show,
  onHide,
  bisonStepId,
  bisonCampaignId,
  stepNumber,
  stepLabel,
  unresolvedVariables,
  renderedFor,
  sampleRenderedFor,
}: Props) {
  const { user } = useAuth()
  // Source the picker from every connected workspace sender, not just the ones
  // already attached to this campaign. Senders are only attached on the Settings
  // step, so before then the campaign list is empty and the user was forced to
  // step forward just to send a test (FIR-7680). Workspace senders are the
  // superset; the campaign list only tells us which are already attached.
  const { data: workspaceResponse, isLoading: isSendersLoading } =
    useGetWorkspaceSenderEmails()
  const { data: campaignResponse } = useGetCampaignSenderEmails(bisonCampaignId)

  const senders = useMemo(
    () => workspaceResponse?.results ?? [],
    [workspaceResponse]
  )
  const attachedSenderIds = useMemo(
    () => new Set((campaignResponse?.results ?? []).map((s) => s.id)),
    [campaignResponse]
  )
  // Warming senders can't be attached, and picking one here would
  // auto-attach it and hit the server rejection. Exclude them from the picker
  // unless they're already attached to this campaign (an admin re-warm of an
  // attached sender), where sending a test stays valid.
  const selectableSenders = useMemo(
    () =>
      senders.filter(
        (sender) =>
          sender.lifecycle_state !== 'warming' ||
          attachedSenderIds.has(sender.id)
      ),
    [senders, attachedSenderIds]
  )
  const senderOptions = useMemo(
    () =>
      selectableSenders.map((sender) => ({
        value: sender.id,
        label: sender.name ? `${sender.name} <${sender.email}>` : sender.email,
      })),
    [selectableSenders]
  )

  // Pre-fill priority: a sender already attached to the campaign (i.e. Settings
  // is done — auto-fill it here) → otherwise the first available workspace
  // sender. Fall back as data loads async without clobbering an explicit pick,
  // avoiding the prop→state effect this would otherwise require.
  const [pickedSenderId, setPickedSenderId] = useState<number | null>(null)
  const defaultSenderId =
    campaignResponse?.results?.[0]?.id ?? selectableSenders[0]?.id ?? null
  const senderId = pickedSenderId ?? defaultSenderId
  const selectedSenderOption =
    senderOptions.find((option) => option.value === senderId) ?? null
  const needsAttach = senderId != null && !attachedSenderIds.has(senderId)
  const [toEmail, setToEmail] = useState<string>(user?.email ?? '')

  const attachMutation = useAttachCampaignSenderEmails(bisonCampaignId)
  const sendMutation = useSendSequenceStepTestEmail()

  // Choose what the test renders against. EmailBison's random-lead endpoint 400s
  // when the campaign has no contacts (FIR-7437), so when there are none we fall
  // back to the caller's sample render through compose (which needs no contact).
  // An explicit per-contact `renderedFor` always wins and skips the count fetch.
  const hasExplicitRender = !!renderedFor
  const { data: leadsResponse, isLoading: isLeadsLoading } =
    useGetCampaignLeads(bisonCampaignId, 1, {
      enabled: show && !hasExplicitRender,
    })
  const noContacts =
    !hasExplicitRender && !isLeadsLoading && leadsResponse?.count === 0
  const awaitingContactCount = !hasExplicitRender && isLeadsLoading
  const effectiveRenderedFor =
    renderedFor ?? (noContacts ? sampleRenderedFor : null) ?? null
  const willSendSample = noContacts && !!sampleRenderedFor
  // Only truly blocked when there's no contact AND no sample to fall back on.
  const blockedNoContacts = noContacts && !sampleRenderedFor

  const hasVariableWarnings = unresolvedVariables.length > 0
  const hasSender = senderId != null
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim())
  const isSubmitting = attachMutation.isPending || sendMutation.isPending
  const canSubmit =
    !hasVariableWarnings &&
    bisonStepId != null &&
    hasSender &&
    // Gate on the workspace query too: senderId can resolve from the campaign
    // query (which loads independently) while the picker still shows its
    // loading placeholder, so without this the button could enable early.
    !isSendersLoading &&
    validEmail &&
    !isSubmitting &&
    // No contact and no sample to render against → EmailBison would 400 (FIR-7437).
    !blockedNoContacts &&
    !awaitingContactCount

  async function handleSubmit() {
    if (!canSubmit || bisonStepId == null || senderId == null) return
    // Reverse auto-fill (FIR-7680): if the chosen sender isn't attached to this
    // campaign yet, attach it before sending. This lets the test send succeed
    // without stepping forward, and — because the attach invalidates the
    // byCampaign query the Settings step also reads — the sender shows up
    // pre-filled in Settings afterward (still adjustable there). Kept in its own
    // try/catch so an attach failure gets a distinct message, not the send one.
    if (needsAttach) {
      try {
        await attachMutation.mutateAsync({ sender_email_ids: [senderId] })
      } catch (err: unknown) {
        toast.error(
          getApiErrorDetail(err, 'Failed to add the sender to this campaign')
        )
        return
      }
    }
    try {
      await sendMutation.mutateAsync({
        bison_step_id: bisonStepId,
        data: {
          sender_email_id: senderId,
          to_email: toEmail.trim(),
          ...(effectiveRenderedFor
            ? {
                rendered_subject: effectiveRenderedFor.subject,
                rendered_body: effectiveRenderedFor.body,
                plain_text: effectiveRenderedFor.plainText,
                recipient_name: effectiveRenderedFor.contactName,
              }
            : {}),
        },
      })
      toast.success(`Test email sent to ${toEmail.trim()}`)
      onHide()
    } catch (err: unknown) {
      toast.error(getApiErrorDetail(err, 'Failed to send test email'))
    }
  }

  const noSenders = !isSendersLoading && selectableSenders.length === 0

  const description = renderedFor
    ? `Sends the email exactly as ${renderedFor.contactName || 'this contact'} would receive it. Variables are already substituted from their contact details.`
    : willSendSample
      ? 'No contacts yet, so this sends with sample details — good for checking formatting and delivery before you add contacts.'
      : 'Sends a real email through your campaign pipeline, so formatting matches what contacts receive. Variables use a random campaign contact.'

  return (
    <Ds2026ButtonProvider>
      <Modal
        open={show}
        onOpenChange={(open: boolean) => !open && onHide()}
        title={
          <span className="text-neutral-800">
            {`Send test email for ${stepLabel ?? `Step ${stepNumber}`}`}
          </span>
        }
        description={<span className="text-neutral-500">{description}</span>}
        onAccept={handleSubmit}
        acceptButtonText="Send test email"
        blockAccept={!canSubmit}
        loading={isSubmitting}
        contentClassName={DS2026_MODAL_CONTENT_CLASS}
        closeIconClassName="rounded-lg hover:text-neutral-500"
        dialogFooterClassName={DS2026_MODAL_FOOTER_CLASS}
        acceptButtonProps={{ className: DS2026_MODAL_BUTTON_CLASS }}
        cancelButtonProps={{ className: DS2026_MODAL_BUTTON_CLASS }}
      >
        <div className={`flex flex-col ${DS2026_MODAL_BODY_CLASS}`}>
          {bisonStepId == null && (
            <Alert
              color="warning"
              description="Save the sequence first. Test sends use the saved step content."
            />
          )}
          {willSendSample && (
            <Alert
              color="info"
              description="No contacts yet, so this test uses sample details. Add a contact to test with real data."
            />
          )}
          {blockedNoContacts && (
            <Alert
              color="warning"
              description="Add at least one contact to this campaign before sending a test. Test emails render against a random campaign contact, so there needs to be one to send."
            />
          )}
          {hasVariableWarnings && (
            <Alert
              color="warning"
              description={
                <>
                  <p>
                    Resolve unfilled variables before sending. Otherwise the
                    test would go out with empty placeholders.
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {unresolvedVariables.map((variable) => (
                      <code
                        key={variable}
                        className="rounded bg-warning-bg px-1.5 py-0.5 text-xxs text-fg-secondary"
                      >
                        {variable}
                      </code>
                    ))}
                  </div>
                </>
              }
            />
          )}
          <div>
            <label
              htmlFor="test-email-sender"
              className="mb-1.5 block text-sm font-medium text-neutral-700"
            >
              Send from
            </label>
            {isSendersLoading ? (
              <div className="flex min-h-[36px] items-center rounded-lg border !border-neutral-300 bg-surface-sunken px-3.5 py-2 text-sm text-fg-secondary">
                Loading senders…
              </div>
            ) : noSenders ? (
              <Alert
                color="warning"
                description="No senders available yet. Add or sync senders in the Settings step before sending a test."
              />
            ) : (
              <>
                <ReactSelectWrapper
                  ds2026
                  styles={ds2026SelectStyles}
                  height="36px"
                  inputId="test-email-sender"
                  instanceId="test-email-sender"
                  menuPosition="fixed"
                  isClearable={false}
                  isSearchable={false}
                  options={senderOptions}
                  value={selectedSenderOption}
                  onChange={(newValue: any) =>
                    setPickedSenderId(newValue?.value ?? null)
                  }
                />
                {needsAttach && (
                  <p className="mt-1 text-xs text-fg-secondary">
                    Choosing this sender for your test email adds them as a
                    sender for this campaign. You can change it in the Settings
                    step.
                  </p>
                )}
              </>
            )}
          </div>
          <div>
            <label
              htmlFor="test-email-recipient"
              className="mb-1.5 block text-sm font-medium text-neutral-700"
            >
              Send to
            </label>
            <input
              id="test-email-recipient"
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="e.g. you@firstbite.io"
              className={dsControlClass(
                toEmail.trim().length > 0 && !validEmail ? 'error' : 'default'
              )}
            />
            {toEmail.trim().length > 0 && !validEmail && (
              <p className="mt-1 text-xs text-error-strong">
                Enter a valid email.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </Ds2026ButtonProvider>
  )
}
